/**
 * ConfirmationDialog - Reusable confirmation modal for destructive operations
 *
 * Story 3.5: Implement Soft Delete Confirmation Dialog
 * AC-3.10: Dialog with warning icon, message, Cancel button (default focus), Delete button (destructive styling)
 * AC-3.39: Reusable for other destructive operations (row deletion, sheet deletion, etc.)
 *
 * Architecture Alignment:
 * - D3: View Architecture - Dialog is presentation layer only
 * - D4: State Management - Dialog state managed in parent component
 */

import React, { useEffect, useRef, type ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export interface ConfirmationDialogProps {
  /** Dialog title */
  title: string;
  /** Dialog message - can be string or React node for complex content */
  message: string | ReactNode;
  /** Label for confirm button (default: "Confirm") */
  confirmLabel?: string;
  /** Label for cancel button (default: "Cancel") */
  cancelLabel?: string;
  /** Variant affects button styling (default: "default") */
  variant?: 'default' | 'destructive';
  /** Callback when user confirms */
  onConfirm: () => void;
  /** Callback when user cancels */
  onCancel: () => void;
  /** Whether dialog is open */
  isOpen?: boolean;
}

/**
 * ConfirmationDialog Component
 *
 * Follows existing modal patterns from DeleteDashboardModal.tsx:
 * - Modal overlay: fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50
 * - Destructive button: bg-red-600 hover:bg-red-700 text-white
 * - AlertTriangle icon for warnings
 */
export function ConfirmationDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
  isOpen = true,
}: ConfirmationDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // AC-3.10: Set default focus to Cancel button per UX best practices
  useEffect(() => {
    if (isOpen && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [isOpen]);

  // AC-3.10: Add keyboard support (Escape to cancel)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const isDestructive = variant === 'destructive';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-dialog-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3
            id="confirmation-dialog-title"
            className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
          >
            {isDestructive && (
              <AlertTriangle size={20} className="text-red-600" />
            )}
            {title}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        </div>

        {/* Message */}
        <div className="text-gray-700 dark:text-gray-300 mb-6">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            ref={cancelButtonRef}
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={
              isDestructive
                ? 'px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold'
                : 'px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold'
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmationDialog;
